import { AlertTriangle, BellRing, CheckCircle2, Power, PowerOff, Wrench, type LucideIcon } from 'lucide-react'

import { DeviceAlertStatus, DeviceStatusType } from '../types'

/** Jedyna konfiguracja statusów urządzenia — etykiety (klucze i18n), ikony i kolory
 *  dla mapy, listy urządzeń i kafli KPI.
 *
 *  Dwie NIEZALEŻNE osie, obie pochodzą z `DeviceStatusType` (`useDeviceData` / SSE):
 *   1) `isWorking: number` (0/1!) — urządzenie włączone / wyłączone,
 *   2) `alertStatus` — 'normal' | 'alarm' | 'error' | 'service'.
 *
 *  Osie się NIE wykluczają: „Wyłączony + Alarm” jest poprawną kombinacją. */

export type StatusPresentation = {
  /** Klucz i18n etykiety — komponenty renderują `t(labelKey)`, nigdy gotowy tekst. */
  labelKey: string
  Icon: LucideIcon
  /** Klasa koloru tekstu/ikony. */
  text: string
  /** Klasa tła chipa (kolor statusu z niską alfą). */
  bg: string
  /** Klasa tła kropki/kwadratu znacznika. */
  dot: string
  /** Kolor jako wartość CSS — dla markerów mapy i wykresów (SVG fill/stroke). */
  color: string
  /** Niższa liczba = ważniejszy stan (sortowanie list, z-index markerów). */
  priority: number
}

/** Oś 2: `DeviceStatusType.alertStatus`. */
export const ALERT_STATUS_CONFIG: Record<DeviceAlertStatus, StatusPresentation> = {
  error: {
    labelKey: 'device.error',
    Icon: AlertTriangle,
    text: 'text-crane-status-error',
    bg: 'bg-crane-status-error/10',
    dot: 'bg-crane-status-error',
    color: 'hsl(var(--crane-status-error))',
    priority: 0,
  },
  alarm: {
    labelKey: 'device.alarm',
    Icon: BellRing,
    text: 'text-crane-status-alarm',
    bg: 'bg-crane-status-alarm/10',
    dot: 'bg-crane-status-alarm',
    color: 'hsl(var(--crane-status-alarm))',
    priority: 1,
  },
  service: {
    labelKey: 'device.service',
    Icon: Wrench,
    text: 'text-crane-status-service',
    bg: 'bg-crane-status-service/10',
    dot: 'bg-crane-status-service',
    color: 'hsl(var(--crane-status-service))',
    priority: 2,
  },
  normal: {
    labelKey: 'device.normal',
    Icon: CheckCircle2,
    text: 'text-crane-status-online',
    bg: 'bg-crane-status-online/10',
    dot: 'bg-crane-status-online',
    color: 'hsl(var(--crane-status-online))',
    priority: 3,
  },
}

/** Oś 1: `DeviceStatusType.isWorking` (0/1). Bez priorytetu — to nie jest stan alarmowy. */
export const WORKING_CONFIG: Record<'on' | 'off', Omit<StatusPresentation, 'priority'>> = {
  on: {
    labelKey: 'device.turnedOn',
    Icon: Power,
    text: 'text-crane-status-online',
    bg: 'bg-crane-status-online/10',
    dot: 'bg-crane-status-online',
    color: 'hsl(var(--crane-status-online))',
  },
  off: {
    labelKey: 'device.turnedOff',
    Icon: PowerOff,
    text: 'text-crane-status-offline',
    bg: 'bg-crane-status-offline/10',
    dot: 'bg-crane-status-offline',
    color: 'hsl(var(--crane-status-offline))',
  },
}

/** Kolejność prezentacyjna (filtry, legenda, kafle KPI) — wg `priority`. */
export const ALL_ALERT_STATUSES: DeviceAlertStatus[] = ['normal', 'alarm', 'error', 'service']

/** Stan alertu urządzenia. `alertStatus` przychodzi z SSE jako wolny string
 *  (`getDeviceStatus` przepisuje wartość `alert` bez walidacji), więc wartość
 *  spoza konfiguracji — tak jak brak statusu — daje 'normal'. */
export const alertStatusOf = (status?: DeviceStatusType): DeviceAlertStatus => {
  const value = status?.alertStatus
  return value && value in ALERT_STATUS_CONFIG ? (value as DeviceAlertStatus) : 'normal'
}

/** `isWorking` to LICZBA (0/1). Brak statusu → wyłączony.
 *  Jedyne dozwolone miejsce zamiany 0/1 na boolean — w propsach zostaje `number`. */
export const isDeviceWorking = (status?: DeviceStatusType): boolean => Boolean(status?.isWorking)

/** Prezentacja stanu alertu — skrót na `ALERT_STATUS_CONFIG[alertStatusOf(status)]`. */
export const alertPresentationOf = (status?: DeviceStatusType): StatusPresentation =>
  ALERT_STATUS_CONFIG[alertStatusOf(status)]

/** Prezentacja stanu włączenia — skrót na `WORKING_CONFIG[...]`. */
export const workingPresentationOf = (status?: DeviceStatusType): Omit<StatusPresentation, 'priority'> =>
  WORKING_CONFIG[isDeviceWorking(status) ? 'on' : 'off']

/** Kolor terminu przeglądu wg `Device.inspections[0]`:
 *  po terminie → alarm, w oknie ostrzegania (`warningDays`) → uwaga, dalej → OK. */
export const inspectionToneClass = (daysLeft: number | null, warningDays = 30): string => {
  if (daysLeft === null) return 'text-crane-muted-foreground'
  if (daysLeft < 0) return 'text-crane-status-alarm'
  if (daysLeft <= warningDays) return 'text-crane-status-attention'
  return 'text-crane-status-online'
}

export const resourceBarClass = (percentage: number): string =>
  percentage < 50 ? 'bg-crane-status-attention' : 'bg-crane-status-online'
